import type { Dispatch, FC, SetStateAction } from 'react'

import { CheckableTag, minWidthFromTheme, TagSize, TagType } from '@sok/design-system'
import { Icon } from 'components/icons'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import styled, { useTheme } from 'styled-components'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { isNonNullable } from 'utils/nullable'
import { trackCustomEvent } from 'utils/tracking/custom-events/custom-events'
import { EventAction, EventCategory } from 'utils/tracking/interfaces/data-layer-events'

const StyledFilterWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacings.medium};
  padding: ${({ theme }) => `0 0 ${theme.spacings.medium} 4px`};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `0 0 ${theme.spacings.medium} 8px`};
  }

  ${({ theme }) => minWidthFromTheme(theme).tablet} {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-bottom: 8px;
  }

  > button,
  > a {
    margin-bottom: ${({ theme }) => theme.spacings.xxSmall};
  }
`

const StyledTagWrapperHorizontallyScrollable = styled.a`
  margin: 4px 0;
  display: flex;
  flex: 0 0 auto;
`

const StyledFilterWrapperHorizontallyScrollable = styled(StyledFilterWrapper)`
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  margin: 4px 0;
  overflow-x: scroll;

  ::-webkit-scrollbar {
    width: 5px;
    height: 4px;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: ${({ theme }) => theme.color.grey};
    height: 3px;
  }
`

interface ToggleFiltersProps {
  showAllFilters: boolean
  setShowAllFilters: Dispatch<SetStateAction<boolean>>
  remainingFiltersLength: number
}

const ToggleFilters: FC<ToggleFiltersProps> = ({
  showAllFilters,
  setShowAllFilters,
  remainingFiltersLength,
}) => {
  const { t } = useTranslation()

  return (
    <CheckableTag
      size={TagSize.MIDDLE}
      type={TagType.DEFAULT}
      checked={false}
      onClick={() => {
        setShowAllFilters(!showAllFilters)
      }}
    >
      {showAllFilters ? (
        <>
          <Icon name="minus-icon" width={16} height={16} margin="0 8px 0 0" />
          {t('See less')}
        </>
      ) : (
        <>
          <Icon name="plus-icon" width={16} height={16} margin="0 8px 0 0" />
          {remainingFiltersLength}
        </>
      )}
    </CheckableTag>
  )
}

const Count = styled.span(({ theme }) => ({
  color: theme.color.darkGrey500,
  marginLeft: theme.spacings.xxxSmall,
}))

interface CategoryLinkItem {
  name?: string
  slug?: string
  count?: number
}

interface SingleFilterProps {
  slug?: string
  item?: CategoryLinkItem
}

const SingleFilter: FC<SingleFilterProps> = ({ item, slug }) => {
  return (
    <CheckableTag
      size={TagSize.MIDDLE}
      type={TagType.DEFAULT}
      checked={item?.slug === slug}
      onClick={() => {
        trackCustomEvent({
          category: EventCategory.PRODUCT_RESULTS,
          action: EventAction.CLICK,
          label: `product-result-filter_${item?.slug}`,
        })
      }}
      data-test-id="product-result-filter"
    >
      <span>{item?.name}</span>
      {item?.count && <Count>({item.count})</Count>}
    </CheckableTag>
  )
}

let lastScrollPosition: number | undefined

interface FiltersProps {
  items?: CategoryLinkItem[]
  parentSlug?: string
  searchParamString?: string
  slug?: string
}

export const CategoryLinks: FC<FiltersProps> = ({
  items,
  parentSlug = '',
  searchParamString = '',
  slug = '',
}) => {
  const isClientSide = useIsClientSide()

  /**
   * All filters are visible during SSR for SEO purposes,
   * but they should be hidden by default client-side.
   */
  const [showAllFilters, setShowAllFilters] = useState(!isClientSide)

  /** Make sure there are no hydration errors */
  useEffect(() => {
    setShowAllFilters(false)
  }, [])

  const theme = useTheme()
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.mobile })
  const isTablet = useMediaQuery({ minWidth: theme.breakpoints.mobile })
  const productFiltersContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isMobile) {
      const element = productFiltersContainerRef?.current
      if (element && lastScrollPosition) {
        element.scrollTo({
          left: lastScrollPosition,
        })
      }

      const handleScrollLeft = () => {
        lastScrollPosition = element?.scrollLeft
      }

      element?.addEventListener('scroll', handleScrollLeft, true)
      return () => {
        element?.removeEventListener('scroll', handleScrollLeft, true)
      }
    }
  }, [isMobile])

  const initialFilters =
    useMemo<CategoryLinkItem[]>(() => {
      const filteredItems = items?.filter(isNonNullable)
      const itemsLength = filteredItems?.length
      if (!showAllFilters && (isMobile || isTablet)) {
        const initialItems = isMobile ? 10 : 15
        return (
          (itemsLength && itemsLength > initialItems
            ? filteredItems?.slice(0, initialItems)
            : filteredItems) || []
        )
      }
      return filteredItems || []
    }, [isMobile, isTablet, items, showAllFilters]) || []

  const remainingFiltersLength = useMemo(() => {
    const initialFiltersLength = initialFilters.length
    const totalFilters = items?.length
    if (totalFilters && initialFiltersLength) {
      return totalFilters - initialFiltersLength
    }
    return 0
  }, [initialFilters.length, items?.length])

  const isSeeMoreButtonVisible = remainingFiltersLength !== 0 || showAllFilters

  if (!showAllFilters && isMobile) {
    return (
      <StyledFilterWrapperHorizontallyScrollable
        data-test-id="product-category-filters"
        ref={productFiltersContainerRef}
      >
        {initialFilters.map((item) => (
          <Link
            key={item?.slug}
            href={`/tuotteet/[...slugs]${searchParamString}`}
            as={`/tuotteet/${item?.slug}${searchParamString}`}
            passHref
          >
            <StyledTagWrapperHorizontallyScrollable tabIndex={-1}>
              <SingleFilter item={item} slug={slug} />
            </StyledTagWrapperHorizontallyScrollable>
          </Link>
        ))}
        <StyledTagWrapperHorizontallyScrollable>
          {isSeeMoreButtonVisible && (
            <ToggleFilters
              showAllFilters={showAllFilters}
              setShowAllFilters={setShowAllFilters}
              remainingFiltersLength={remainingFiltersLength}
            />
          )}
        </StyledTagWrapperHorizontallyScrollable>
      </StyledFilterWrapperHorizontallyScrollable>
    )
  }

  return (
    <StyledFilterWrapper data-test-id="product-category-filters">
      {initialFilters.map((item) => (
        <Link
          key={item?.slug}
          href={`/tuotteet/[...slugs]${searchParamString}`}
          as={`/tuotteet/${
            slug === item?.slug
              ? // The current link is active, and clicking on it again
                // should "uncheck" it, or navigate back to parent
                parentSlug
              : item?.slug
          }${searchParamString}`}
          passHref
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a>
            <SingleFilter item={item} slug={slug} />
          </a>
        </Link>
      ))}

      {isSeeMoreButtonVisible && (
        <ToggleFilters
          showAllFilters={showAllFilters}
          setShowAllFilters={setShowAllFilters}
          remainingFiltersLength={remainingFiltersLength}
        />
      )}
    </StyledFilterWrapper>
  )
}
